import { Fragment } from 'react'

import { cn } from '@/utils'
import {
  COVERAGE_CONTINUATION_CLASS,
  COVERAGE_SWATCH_CLASS,
  coverageTextClass,
  type StatusFor,
} from './coverage'
import { spanColumns, spanLabel, spanTitle } from './MapSpan'
import type { MapSpan } from './map'
import {
  fadedClass,
  type Highlight,
  type MapActions,
  OPEN_RING,
  TARGET_UNDER_POINTER,
} from './mapView'
import { LINKED_STITCHED_BAR } from './memberLink'
import { StatementButton } from './StatementButton'

interface StitchedSpanProps {
  span: MapSpan
  gridRow: string
  // The account number of each row the card runs down, top to bottom.
  rowNumbers: (string | null)[]
  laneLabel: string
  choosing: boolean
  openStatementId: string | null
  highlight: Highlight
  statusFor: StatusFor
  actions: MapActions
}

// The carried pages mark the button, and the button is see-through between the rows, so the bars
// take the drop colour from it.
const BAR_DROPPING_ON =
  'group-data-[drop-over]/stitch:bg-blue-600 group-data-[drop-over]/stitch:text-white'

// One statement over several account rows: a bar in each row, joined by a hairline. It is one
// button, so a click anywhere on it opens the statement and the whole shape shows as open. The
// page count is the statement's, so it shows once, on the top bar. Each bar is that account's
// verdict, since one account on a statement can reconcile while another does not.
export const StitchedSpan = ({
  span,
  gridRow,
  rowNumbers,
  laneLabel,
  choosing,
  openStatementId,
  highlight,
  statusFor,
  actions,
}: StitchedSpanProps) => {
  const { view } = span
  const statuses = rowNumbers.map((number) =>
    statusFor(view.statement.id, number),
  )
  const open = openStatementId === view.statement.id
  const faded = (index: number) =>
    fadedClass(highlight, statuses[index] ?? null)

  return (
    <div className="h-full" style={{ gridColumn: spanColumns(span), gridRow }}>
      <StatementButton
        view={view}
        choosing={choosing}
        actions={actions}
        className={cn(
          'group/stitch flex size-full cursor-pointer flex-col rounded-md font-medium tabular-nums text-[11px] leading-none',
          choosing ? TARGET_UNDER_POINTER : 'hover:opacity-80',
        )}
        title={spanTitle(
          laneLabel,
          view,
          statusFor(view.statement.id, null),
          choosing,
        )}
      >
        {statuses.map((status, index) => (
          <Fragment key={rowNumbers[index] ?? index}>
            {index > 0 ? (
              <span
                className={cn(
                  'mx-auto w-px flex-1',
                  open ? 'bg-neutral-900' : 'bg-neutral-400',
                  faded(index - 1) && faded(index),
                )}
              />
            ) : null}
            <span
              className={cn(
                'flex h-8 shrink-0 items-center justify-center overflow-hidden rounded-md px-1 transition-colors',
                span.continuesBefore && 'rounded-l-none',
                span.continuesAfter && 'rounded-r-none',
                index === 0
                  ? COVERAGE_SWATCH_CLASS[status]
                  : COVERAGE_CONTINUATION_CLASS[status],
                coverageTextClass(status),
                open && OPEN_RING,
                LINKED_STITCHED_BAR,
                BAR_DROPPING_ON,
                faded(index),
              )}
            >
              {index === 0 ? (
                <span className="truncate">{spanLabel(span)}</span>
              ) : null}
            </span>
          </Fragment>
        ))}
      </StatementButton>
    </div>
  )
}
